package co.uk.paxes.pinpoint.web

import co.uk.paxes.pinpoint.model.PinColor
import co.uk.paxes.pinpoint.model.Position
import co.uk.paxes.pinpoint.model.Pin
import co.uk.paxes.pinpoint.model.Player
import kotlin.js.Date
import kotlin.js.json

/**
 * Saved game state for PlayGama storage
 * Supports AI games and local multiplayer games
 */
data class GameSaveState(
    val version: Int = 1,
    val gameMode: String,  // "AI" or "LOCAL_MULTIPLAYER"
    val boardSeed: Long,
    val dressColors: List<String>,  // ["RED", "GREEN", "PURPLE", "YELLOW"]
    val boardPins: List<SavedPin>,
    val players: List<SavedPlayer>,
    val currentPlayerIndex: Int,  // Index into players array
    val currentHandNumber: Int,
    val lastSaveTimestamp: Long,
    val currentHand: List<String> = emptyList()  // Colors of remaining pins in current hand
) {
    companion object {
        fun fromJson(jsonInput: dynamic): GameSaveState? {
            if (jsonInput == null) return null
            return try {
                val parsed: dynamic = if (js("typeof jsonInput === 'string'")) {
                    JSON.parse(jsonInput as String)
                } else {
                    jsonInput
                }
                
                if (parsed == null) return null
                
                val dressColorsRaw = parsed["dressColors"] ?: parsed["dressColors_1"]
                val dressColorsList = mutableListOf<String>()
                if (dressColorsRaw != null) {
                    val rawArr: dynamic = if (js("Array.isArray(dressColorsRaw)")) dressColorsRaw else dressColorsRaw["array_1"]
                    if (rawArr != null) {
                        for (i in 0 until rawArr.length) {
                            val color = rawArr[i] as? String
                            if (color != null) dressColorsList.add(color)
                        }
                    }
                }
                
                val boardPinsRaw = parsed["boardPins"] ?: parsed["boardPins_1"]
                val boardPinsList = mutableListOf<SavedPin>()
                if (boardPinsRaw != null) {
                    val rawArr: dynamic = if (js("Array.isArray(boardPinsRaw)")) boardPinsRaw else boardPinsRaw["array_1"]
                    if (rawArr != null) {
                        for (i in 0 until rawArr.length) {
                            val pin = SavedPin.fromJson(rawArr[i])
                            if (pin != null) boardPinsList.add(pin)
                        }
                    }
                }
                
                val playersRaw = parsed["players"] ?: parsed["players_1"]
                val playersList = mutableListOf<SavedPlayer>()
                if (playersRaw != null) {
                    val rawArr: dynamic = if (js("Array.isArray(playersRaw)")) playersRaw else playersRaw["array_1"]
                    if (rawArr != null) {
                        for (i in 0 until rawArr.length) {
                            val player = SavedPlayer.fromJson(rawArr[i])
                            if (player != null) playersList.add(player)
                        }
                    }
                }
                
                val rawVer = parsed["version"] ?: parsed["version_1"]
                val rawMode = parsed["gameMode"] ?: parsed["gameMode_1"]
                val rawSeed = parsed["boardSeed"] ?: parsed["boardSeed_1"]
                val rawCurIdx = parsed["currentPlayerIndex"] ?: parsed["currentPlayerIndex_1"]
                val rawHandNum = parsed["currentHandNumber"] ?: parsed["currentHandNumber_1"]
                val rawTime = parsed["lastSaveTimestamp"] ?: parsed["lastSaveTimestamp_1"]
                val rawCurrentHand = parsed["currentHand"] ?: parsed["currentHand_1"]

                val version = js("parseInt(rawVer, 10) || 1") as Int
                val gameMode = (rawMode as? String) ?: "LOCAL_MULTIPLAYER"
                val boardSeedDouble = js("Number(rawSeed) || 0") as Double
                val boardSeed = boardSeedDouble.toLong()
                val currentPlayerIndex = js("parseInt(rawCurIdx, 10) || 0") as Int
                val currentHandNumber = js("parseInt(rawHandNum, 10) || 1") as Int
                val lastSaveTimestampDouble = js("Number(rawTime) || 0") as Double
                val nowMs = Date.now().toLong()
                val lastSaveTimestamp = if (lastSaveTimestampDouble > 0) lastSaveTimestampDouble.toLong() else nowMs

                // Parse current hand (list of color strings)
                val currentHandList = mutableListOf<String>()
                if (rawCurrentHand != null) {
                    val handArr: dynamic = if (js("Array.isArray(rawCurrentHand)")) rawCurrentHand else rawCurrentHand["array_1"]
                    if (handArr != null) {
                        for (i in 0 until handArr.length) {
                            val color = handArr[i] as? String
                            if (color != null) currentHandList.add(color)
                        }
                    }
                }
                
                GameSaveState(
                    version = version,
                    gameMode = gameMode,
                    boardSeed = boardSeed,
                    dressColors = dressColorsList,
                    boardPins = boardPinsList,
                    players = playersList,
                    currentPlayerIndex = currentPlayerIndex,
                    currentHandNumber = currentHandNumber,
                    lastSaveTimestamp = lastSaveTimestamp,
                    currentHand = currentHandList
                )
            } catch (e: Exception) {
                console.error("Failed to parse GameSaveState: ${e.message}")
                null
            }
        }
    }
    
    fun toJson(): String {
        val dressColorsArray = dressColors.toTypedArray()
        val boardPinsArray = boardPins.map { it.toDynamic() }.toTypedArray()
        val playersArray = players.map { it.toDynamic() }.toTypedArray()
        val currentHandArray = currentHand.toTypedArray()
        
        val obj = json(
            "version" to version,
            "gameMode" to gameMode,
            "boardSeed" to boardSeed,
            "dressColors" to dressColorsArray,
            "boardPins" to boardPinsArray,
            "players" to playersArray,
            "currentPlayerIndex" to currentPlayerIndex,
            "currentHandNumber" to currentHandNumber,
            "lastSaveTimestamp" to lastSaveTimestamp,
            "currentHand" to currentHandArray
        )
        return JSON.stringify(obj)
    }
}

/**
 * Saved pin state
 */
data class SavedPin(
    val sectionRow: Int,  // 0-2: section row
    val sectionCol: Int,  // 0-2: section col
    val localRow: Int,    // 0-3: local row within section
    val localCol: Int,    // 0-3: local col within section
    val color: String,
    val isJoker: Boolean,
    val isReversed: Boolean,
    val isJokerTurnedBlack: Boolean
) {
    fun toDynamic(): dynamic {
        return json(
            "sectionRow" to sectionRow,
            "sectionCol" to sectionCol,
            "localRow" to localRow,
            "localCol" to localCol,
            "color" to color,
            "isJoker" to isJoker,
            "isReversed" to isReversed,
            "isJokerTurnedBlack" to isJokerTurnedBlack
        )
    }

    companion object {
        fun fromJson(json: dynamic): SavedPin? {
            if (json == null) return null
            return try {
                val sRow = json["sectionRow"] ?: json["sectionRow_1"]
                val sCol = json["sectionCol"] ?: json["sectionCol_1"]
                val lRow = json["localRow"] ?: json["localRow_1"]
                val lCol = json["localCol"] ?: json["localCol_1"]
                val col = json["color"] ?: json["color_1"]
                val jok = json["isJoker"] ?: json["isJoker_1"]
                val rev = json["isReversed"] ?: json["isReversed_1"]
                val blk = json["isJokerTurnedBlack"] ?: json["isJokerTurnedBlack_1"]

                val sectionRow = js("parseInt(sRow, 10) || 0") as Int
                val sectionCol = js("parseInt(sCol, 10) || 0") as Int
                val localRow = js("parseInt(lRow, 10) || 0") as Int
                val localCol = js("parseInt(lCol, 10) || 0") as Int
                val color = (col as? String) ?: "RED"
                val isJoker = (jok == true || jok == "true")
                val isReversed = (rev == true || rev == "true")
                val isJokerTurnedBlack = (blk == true || blk == "true")

                SavedPin(
                    sectionRow = sectionRow,
                    sectionCol = sectionCol,
                    localRow = localRow,
                    localCol = localCol,
                    color = color,
                    isJoker = isJoker,
                    isReversed = isReversed,
                    isJokerTurnedBlack = isJokerTurnedBlack
                )
            } catch (e: Exception) {
                console.error("Failed to parse SavedPin: ${e.message}")
                null
            }
        }
    }
    
    fun toPin(): Pin {
        return Pin(
            color = PinColor.valueOf(color),
            isJoker = isJoker,
            isReversed = isReversed,
            isJokerTurnedBlack = isJokerTurnedBlack
        )
    }
    
    fun toPosition(): Position {
        return Position(sectionRow, sectionCol, localRow, localCol)
    }
}

/**
 * Saved player state
 */
data class SavedPlayer(
    val id: String,
    val name: String,
    val score: Int,
    val jokersUsed: Int = 0,
    val isAI: Boolean = false
) {
    fun toDynamic(): dynamic {
        return json(
            "id" to id,
            "name" to name,
            "score" to score,
            "jokersUsed" to jokersUsed,
            "isAI" to isAI
        )
    }

    companion object {
        fun fromJson(json: dynamic): SavedPlayer? {
            if (json == null) return null
            return try {
                val rawId = json["id"] ?: json["id_1"]
                val rawName = json["name"] ?: json["name_1"]
                val rawScore = json["score"] ?: json["score_1"]
                val rawJokers = json["jokersUsed"] ?: json["jokersUsed_1"]
                val rawIsAI = json["isAI"] ?: json["isAI_1"]

                val id = (rawId as? String) ?: ""
                val name = (rawName as? String) ?: "Player"
                val score = js("parseInt(rawScore, 10) || 0") as Int
                val jokersUsed = js("parseInt(rawJokers, 10) || 0") as Int
                val isAI = (rawIsAI == true || rawIsAI == "true")

                SavedPlayer(
                    id = id,
                    name = name,
                    score = score,
                    jokersUsed = jokersUsed,
                    isAI = isAI
                )
            } catch (e: Exception) {
                console.error("Failed to parse SavedPlayer: ${e.message}")
                null
            }
        }
    }
    
    fun toPlayer(): Player {
        return Player(
            id = id,
            name = name,
            score = score,
            jokersUsed = jokersUsed,
            isAI = isAI
        )
    }
}

/**
 * Save metadata for UI display
 */
data class SaveMetadata(
    val timestamp: Long,
    val playerNames: List<String>,
    val gameMode: String,
    val playerCount: Int
) {
    fun getTimeAgo(): String {
        val now = Date.now().toLong()
        val diffMs = now - timestamp
        val diffMins = (diffMs / 1000 / 60).toInt()
        val diffHours = diffMins / 60
        val diffDays = diffHours / 24
        
        return when {
            diffMins < 1 -> "just now"
            diffMins < 60 -> "$diffMins minute${if (diffMins > 1) "s" else ""} ago"
            diffHours < 24 -> "$diffHours hour${if (diffHours > 1) "s" else ""} ago"
            diffDays == 1 -> "yesterday"
            diffDays < 7 -> "$diffDays days ago"
            else -> "${diffDays / 7} week${if (diffDays >= 14) "s" else ""} ago"
        }
    }
}
